import React, { useEffect, useState } from 'react';
import { RootState, useAppSelector } from '@Store/store';
import { useInboxConversations, useChatMessages } from './hooks';
import Sidebar from './components/Sidebar';
import ChatWindow from './components/ChatWindow';
import { useSendInstagramMessageMutation } from './apiSlice';
import { toast } from 'react-toastify';
import { Menu, ArrowLeft } from 'lucide-react';
import { useFirebaseAuth } from './useFirebaseAuth';

const Inbox: React.FC = () => {
    const { user } = useAppSelector((state: RootState) => state.profile);

    // State for selected page (IG Account or "all")
    const [selectedPageId, setSelectedPageId] = useState<string | "all">("all");

    // State for selected conversation
    const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

    // Mobile specific state
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [showSidebar, setShowSidebar] = useState(true);

    // Firebase Authentication
    // The hook handles sign-in on mount and sign-out on unmount
    const { loading: authLoading, error: authError } = useFirebaseAuth();

    useEffect(() => {
        const handleResize = () => setIsMobileView(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Derived: The actual IDs to query. If 'all', pass the array of user's IG IDs.
    const connectedPageIds = user?.ig_ids?.map((ig: any) => ig.igsid) || [];
    const queryPageIds = selectedPageId === "all" ? connectedPageIds : selectedPageId;

    // Fetch conversations (supports string or array)
    // ONLY fetch if auth is ready!
    const { conversations, loading: loadingConversations, loadMore } = useInboxConversations(
        !authLoading && !authError ? queryPageIds : null
    );

    // Get selected conversation object
    const selectedConversation = conversations.find(c => c.id === selectedConversationId) || null;

    // Fetch messages for selected conversation
    const { messages, loading: loadingMessages } = useChatMessages(selectedConversationId);

    // Mutation for sending messages
    const [sendMessage, { isLoading: isSending }] = useSendInstagramMessageMutation();

    const handleSendMessage = async (text: string) => {
        if (!selectedConversation) return;

        try {
            await sendMessage({
                id: selectedConversation.user_id, // Recipient
                ig_id: selectedConversation.page_id, // Sender (Page ID)
                text: text
            }).unwrap();

        } catch (error: any) {
            console.error("Failed to send message:", error);
            toast.error(error?.data?.message || "Failed to send message");
            throw error;
        }
    };

    // Handler for selecting conversation on mobile
    const onSelectConversation = (id: string) => {
        setSelectedConversationId(id);
        if (isMobileView) {
            setShowSidebar(false);
        }
    };

    const onBackToSidebar = () => {
        setShowSidebar(true);
        setSelectedConversationId(null);
    };

    // Show loading state while authenticating with Firebase
    if (authLoading) {
        return (
            <div className="flex h-full items-center justify-center bg-white w-full">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-500 text-sm">Connecting to secure inbox...</p>
                </div>
            </div>
        );
    }

    if (authError) {
        return (
            <div className="flex h-full items-center justify-center bg-white w-full">
                <div className="text-center p-8 max-w-md">
                    <div className="bg-red-50 text-red-500 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">Connection Failed</h3>
                    <p className="text-gray-500 mb-6">We couldn't securely connect to your inbox. Please try refreshing the page.</p>
                    <button onClick={() => window.location.reload()} className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                        Refresh Page
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full overflow-hidden bg-white w-full relative">

            {/* Left Sidebar - Hidden on mobile if chat is open */}
            <div
                className={`
                    w-full md:w-[350px] flex-shrink-0 h-full border-r border-gray-100 bg-white
                    absolute md:relative z-20 transition-transform duration-300 ease-in-out
                    ${isMobileView ? (showSidebar ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'}
                `}
            >
                <Sidebar
                    conversations={conversations}
                    selectedId={selectedConversationId}
                    onSelect={onSelectConversation}
                    loading={loadingConversations}
                    connectedPages={user?.ig_ids || []}
                    selectedPageId={selectedPageId}
                    onSelectPage={setSelectedPageId}
                    onLoadMore={loadMore}
                />
            </div>

            {/* Chat Area - Full screen on mobile when active */}
            <div
                className={`
                    flex-1 h-full min-w-0 bg-gray-50/30 w-full
                    absolute md:relative z-10 transition-transform duration-300 ease-in-out
                    ${isMobileView ? (showSidebar ? 'translate-x-full' : 'translate-x-0') : 'translate-x-0'}
                `}
            >
                {/* Mobile Header for "Back" */}
                {isMobileView && !showSidebar && (
                    <div className="flex items-center p-3 bg-white border-b border-gray-100 sticky top-0 z-30">
                        <button onClick={onBackToSidebar} className="p-2 mr-2 rounded-full hover:bg-gray-100">
                            <ArrowLeft size={20} className="text-gray-600" />
                        </button>
                        <span className="font-semibold text-gray-800 truncate">
                            {selectedConversation?.user_name || "Chat"}
                        </span>
                    </div>
                )}

                <ChatWindow
                    conversation={selectedConversation}
                    messages={messages}
                    loading={loadingMessages}
                    onSendMessage={handleSendMessage}
                    sending={isSending}
                />
            </div>
        </div>
    );
};

export default Inbox;